// @flow

import * as React from 'react';
import {defineMessages, useIntl, FormattedMessage} from 'react-intl';
import Button from 'sail/Button';
import Group from 'sail/Group';
import {BodyMono, BodyAltMono} from 'sail/Text';
import {useAnalytics} from 'docs/data';

import {useStripeClui} from 'docs/components/contrib/StripeClui/helpers/react/provider';
import SubscriptionLifecycleSvgDiagram from './SubscriptionLifecycleSvgDiagram';

import './SubscriptionLifecycleDiagram.scss';

const messages = defineMessages({
  startVisualization: {
    id: 'docsApp.subscriptionLifecycleDiagram.startVisualization',
    description:
      'Label on the action to start the visualization of a subscription lifecycle via the Stripe Shell',
    defaultMessage: 'Visualize with Stripe Shell',
  },
  resetVisualization: {
    id: 'docsApp.subscriptionLifecycleDiagram.resetVisualization',
    description:
      'Label on the action to reset the visualization of a subscription lifecycle via the Stripe Shell',
    defaultMessage: 'Reset diagram',
  },
  instructions: {
    id: 'docsApp.subscriptionLifecycleDiagram.instructions',
    description:
      'Instructions for the user to click diagram elements to start a visualization.',
    defaultMessage: `
In the diagram, click <Emphasize>Payment succeeds</Emphasize> or
<Emphasize>Payment fails</Emphasize> to see the API calls and webhook
events associated with the lifecycle of a subscription. You can reset
the diagram at any time by either closing this shell or clicking the
<Emphasize>Reset diagram</Emphasize> button.
`,
  },
});

type DiagramPhase =
  | 'initial'
  | 'interactive'
  | 'successLoading'
  | 'successComplete'
  | 'failLoading'
  | 'failComplete';

const Instructions = () => (
  <div className="DiagramInstructions">
    <BodyMono color="gray200">
      <FormattedMessage
        {...messages.instructions}
        values={{
          Emphasize: (label) => (
            <BodyAltMono color="white">{label}</BodyAltMono>
          ),
        }}
      />
    </BodyMono>
  </div>
);

export const SubscriptionLifecycleDiagram = () => {
  const [phase, setPhase] = React.useState<DiagramPhase>('initial');
  const {formatMessage} = useIntl();
  const {trackAction} = useAnalytics();
  const scrollToRef = React.useRef();

  const {
    state: {panes, openState},
    open,
    close,
    updatePane,
    printOutput,
    addPane,
    invokeCommand,
    reset,
    lockPanes,
    unlockPanes,
  } = useStripeClui();

  // Effect which will track when the diagram switches between phases...
  React.useEffect(() => {
    trackAction(`subscription_diagram_phase_${phase}`);
  }, [phase, trackAction]);

  const handleSuccessComplete = React.useCallback(() => {
    setPhase('successComplete');
    unlockPanes();
  }, [setPhase, unlockPanes]);

  const handleFailComplete = React.useCallback(() => {
    setPhase('failComplete');
    unlockPanes();
  }, [setPhase, unlockPanes]);

  React.useEffect(() => {
    const triggerPaneUnlocked = panes?.[1]?.allowInput ?? false;

    if (phase === 'successLoading' && triggerPaneUnlocked) {
      handleSuccessComplete();
    }

    if (phase === 'failLoading' && triggerPaneUnlocked) {
      handleFailComplete();
    }
  }, [phase, panes, handleSuccessComplete, handleFailComplete]);

  // Scroll to the top of the diagram once the user clicks on a node...
  React.useEffect(() => {
    if (
      scrollToRef.current &&
      ['successLoading', 'failLoading'].includes(phase)
    ) {
      // Need to wait until element is present before scrolling into view.
      setTimeout(() => {
        if (scrollToRef.current) {
          scrollToRef.current.scrollIntoView({behavior: 'smooth'});
        }
      }, 0);
    }
  }, [phase]);

  const handleInitialize = () => {
    reset();
    updatePane({paneIdx: 0, pane: {showWelcomePrompt: false}});

    printOutput({
      output: <Instructions />,
    });

    open();

    invokeCommand({command: 'stripe listen'});

    lockPanes();

    setPhase('interactive');
  };

  const handleSuccessClick = () => {
    addPane();
    invokeCommand({command: 'stripe trigger subscription.payment_succeeded'});

    setPhase('successLoading');
  };

  const handleFailClick = () => {
    addPane();
    invokeCommand({command: 'stripe trigger subscription.payment_failed'});

    setPhase('failLoading');
  };

  const handleReset = React.useCallback(() => {
    setPhase('initial');

    reset();
    unlockPanes();
    close();
    // Need to disable dependencies check because the functions
    // received change every render (causing the effect to recurse
    // unnecessarily) but their logic does not change...
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Reset the diagram / shell when the user closes...
  React.useEffect(() => {
    if (openState === 'closed') {
      handleReset();
    }
  }, [openState, handleReset]);

  return (
    <div className="SubscriptionLifecycleDiagram" ref={scrollToRef}>
      <Group
        direction="vertical"
        alignItems="stretch"
        justifyContent="flex-start"
      >
        <Button
          {...(phase === 'initial' ? {icon: 'api'} : {})}
          label={formatMessage(
            phase === 'initial'
              ? messages.startVisualization
              : messages.resetVisualization,
          )}
          size="jumbo"
          color="blue"
          className="VisualizeButton"
          onClick={phase === 'initial' ? handleInitialize : handleReset}
        />
        <SubscriptionLifecycleSvgDiagram
          phase={phase}
          {...(phase === 'interactive'
            ? {
                onSuccessClick: handleSuccessClick,
                onFailClick: handleFailClick,
              }
            : {})}
        />
      </Group>
    </div>
  );
};
